import { getService } from 'reducers/service';
import ServiceName from '../../shared/constants/serviceName';
import { getPersonById, getPlaceById } from '../../shared/services/generalServices';

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const getPersonHistory = (personId, { page = 1, pageSize = 25 } = {}) =>
  getService(ServiceName.RecordPersonHistory).find({
    query: { ptsPersonID: personId, page, pageSize },
  });

export const getPersonHistorySnapshots = (personId, { page = 1, pageSize = 25 } = {}) =>
  getService(ServiceName.RecordPersonHistorySnapshots).find({
    query: { ptsPersonID: personId, page, pageSize },
  });

export const getPersonImages = async (personId, isMarkOnly) => {
  const service = getService(ServiceName.CadPersonPhoto);
  return service.get(personId, { query: { IsMarkOnly: isMarkOnly } });
};

export const editPersonImage = async (imageId, data) => {
  const service = getService(ServiceName.CadPersonPhoto);
  return service.patch(imageId, data);
};

export const deletePersonImage = async (imageId) => {
  const service = getService(ServiceName.CadPersonPhoto);
  return service.remove(imageId);
};

export const uploadPersonPhoto = async ({ file, ...rest }) => {
  const service = getService(ServiceName.CadUploadPersonPhoto);
  const fileName = file?.name ? file.name.substring(0, 49) : 'Snapshot';
  const fileType = file?.name ? file.name.split('.').pop() : 'png';
  const data = await readFileAsBase64(file);

  return service.create({ ...rest, fileName, fileType, data });
};

export const getPersonImage = async (imageId) => {
  const service = getService(ServiceName.CadUploadPersonPhoto);
  return service.get(imageId);
};

export const createPersonMark = async (data) => {
  const service = getService(ServiceName.CadUploadPersonPhoto);
  return service.create(data);
};

export const getPersonRecordDetails = async (personId, excludeEmployeeRelated) => {
  const service = getService(ServiceName.SearchPersonRelatedRecord);
  return service.get(personId, { query: { excludeEmployeeRelated } });
};

export const getPersonDetails = async (ptsPersonID) => {
  const service = getService(ServiceName.ptsPerson);

  const response = await service.find({
    query: {
      ptsPersonID,
      IsDeleted: false,
    },
  });

  return response?.data || [];
};

export const createPerson = async (payload) => {
  const service = getService(ServiceName.CadSearchPeople);
  const response = await service.create(payload);
  return response?.data || response;
};

export const editPerson = async (ptsPersonID, payload) => {
  const service = getService(ServiceName.CadSearchPeople);
  const response = await service.patch(ptsPersonID, payload);
  return response?.data || response;
};

export const getPeopleContactMethods = async (ptsPersonID) => {
  const service = getService(ServiceName.RecordPersonContact);
  const response = await service.find({
    query: {
      ptsParentID: ptsPersonID,
      PartyType: 'Person',
      IsDeleted: false,
      Info: { $ne: '' },
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};

export const createPersonContact = async (payload) => {
  const service = getService(ServiceName.RecordPersonContact);
  const response = await service.create(payload);
  return response?.data || response;
};

export const editPersonContact = async (ptsInfoID, payload) => {
  const service = getService(ServiceName.RecordPersonContact);
  const response = await service.patch(ptsInfoID, payload);
  return response?.data || response;
};

export const deletePersonContact = async (ptsInfoID) => {
  const service = getService(ServiceName.RecordPersonContact);
  const response = await service.remove(ptsInfoID);
  return response?.data || response;
};

export const getPeopleEmployments = async (ptsPersonID) => {
  const service = getService(ServiceName.RecordPersonEmployment);
  const response = await service.find({
    query: {
      ptsPersonID,
      IsDeleted: false,
      $sort: { Created: -1 },
    },
  });
  const rows = response?.data || response || [];

  return rows.map((row) => {
    const place = Array.isArray(row.Place) && row.Place.length ? row.Place[0] : null;
    return {
      ...row,
      PlaceName: place?.PlaceName || null,
      PlaceType: place?.PlaceType || null,
      rowKey: `${row.ptsPersonID}-${row.ptsPlaceID}`,
    };
  });
};

export const createPersonEmployment = async (payload) => {
  const service = getService(ServiceName.CadSearchPeopleEmployment);
  const response = await service.create(payload);
  return response?.data || response;
};

export const editPersonEmployment = async (compositeKey, payload) => {
  const service = getService(ServiceName.CadSearchPeopleEmployment);
  const response = await service.patch(compositeKey, payload);
  return response?.data || response;
};

export const deletePersonEmployment = async (compositeKey) => {
  const service = getService(ServiceName.CadSearchPeopleEmployment);
  const response = await service.remove(compositeKey);
  return response?.data || response;
};

export const getPeopleFeatures = async (ptsPersonID) => {
  const service = getService(ServiceName.RecordPersonFeature);
  const response = await service.find({
    query: {
      ptsPersonID,
      IsDeleted: false,
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};

export const createPersonFeature = async (payload) => {
  const service = getService(ServiceName.CadPersonFeatures);
  const response = await service.create(payload);
  return response?.data || response;
};

export const editPersonFeature = async (ptsFeatureID, payload) => {
  const service = getService(ServiceName.CadPersonFeatures);
  const response = await service.patch(ptsFeatureID, payload);
  return response?.data || response;
};

export const deletePersonFeature = async (ptsFeatureID) => {
  const service = getService(ServiceName.CadPersonFeatures);
  const response = await service.remove(ptsFeatureID);
  return response?.data || response;
};

export const getPersonDnaSamples = async (ptsPersonID) => {
  const service = getService(ServiceName.CadPersonDnaSamples);
  const samples = (await service.get(ptsPersonID)) || [];

  return Promise.all(
    samples.map(async (sample) => {
      const [crimeLabPlace, takenByPerson] = await Promise.all([
        sample.ptsCrimeLabPlaceID ? getPlaceById(sample.ptsCrimeLabPlaceID) : null,
        sample.ptsTakenByPersonID ? getPersonById(sample.ptsTakenByPersonID) : null,
      ]);

      return {
        ...sample,
        CrimeLab: crimeLabPlace?.PlaceName || null,
        TakenBy: takenByPerson?.FullName || null,
      };
    })
  );
};

export const createDnaSample = async (payload) => {
  const service = getService(ServiceName.CadPersonDnaSamples);
  const response = await service.create(payload);
  return response?.data || response;
};

export const editDnaSample = async (ptsDNASampleID, payload) => {
  const service = getService(ServiceName.CadPersonDnaSamples);
  const response = await service.patch(ptsDNASampleID, payload);
  return response?.data || response;
};

export const deleteDnaSample = async (ptsDNASampleID) => {
  const service = getService(ServiceName.CadPersonDnaSamples);
  const response = await service.remove(ptsDNASampleID);
  return response?.data || response;
};

export const getPeopleAssociates = async (ptsPersonID) => {
  const service = getService(ServiceName.CadPersonAssociate);
  const response = await service.find({ query: { ptsPersonID } });
  return response?.data || response || [];
};

export const createPersonAssociate = async (payload) => {
  const service = getService(ServiceName.RecordPersonAssociates);
  const response = await service.create(payload);
  return response?.data || response;
};

export const updatePersonAssociate = async (compositeKey, payload) => {
  const service = getService(ServiceName.RecordPersonAssociates);
  const response = await service.update(compositeKey, payload);
  return response?.data || response;
};

export const deletePersonAssociate = async (compositeKey) => {
  const service = getService(ServiceName.RecordPersonAssociates);
  const response = await service.remove(compositeKey);
  return response?.data || response;
};

export const getPeopleTags = async (ptsPersonID) => {
  const service = getService(ServiceName.RmsTagsV2);
  const response = await service.find({
    query: {
      ptsParentID: ptsPersonID,
      ParentType: 'Person',
      IsDeleted: false,
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};

export const savePersonTag = async (payload) => {
  const service = getService(ServiceName.RmsTagsV2);
  if (payload.ptsTagID) {
    return service.update({ ptsTagID: payload.ptsTagID, IsDeleted: false }, payload);
  }
  return service.create(payload);
};

export const removePersonTag = async (ptsTagID) => {
  const service = getService(ServiceName.RmsTagsV2);
  return service.remove(null, { query: { ptsTagID, IsDeleted: false } });
};

export const getCities = async () => {
  const service = getService(ServiceName.CityManagement);
  const response = await service.find({ query: { $limit: 1000 } });
  return response?.data || response || [];
};

export const createCityRecord = async (payload) => {
  const service = getService(ServiceName.CityManagement);
  const response = await service.create(payload);
  return response?.data || response;
};

export const updateCityRecord = async (ptsCityID, payload) => {
  const service = getService(ServiceName.CityManagement);
  const response = await service.patch(ptsCityID, payload);
  return response?.data || response;
};

export const deleteCityRecord = async (ptsCityID) => {
  const service = getService(ServiceName.CityManagement);
  return service.remove(ptsCityID);
};

export const getPersonActiveWarrants = async (ptsPersonID) => {
  const service = getService(ServiceName.CadSearchWarrants);
  return service.get(ptsPersonID);
};
